/**
 * Modal — Single overlay pattern for all record flows (Design System §5)
 *
 * - Uses the dedicated shadow-overlay token
 * - Slides up with the sheet motion token
 * - Traps focus; closes on Escape and backdrop click
 * - Accessible: role=dialog, aria-modal, aria-labelledby
 */

import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Width class: 'sm' = 480px, 'md' = 640px (default), 'lg' = 800px */
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  /** Footer content, e.g. action buttons */
  footer?: React.ReactNode;
  /** Optional accessible context announced after the title. */
  description?: React.ReactNode;
  /** Dialog, bottom sheet, or right-edge detail panel presentation. */
  presentation?: 'dialog' | 'sheet' | 'detail';
  className?: string;
}

const WIDTH: Record<NonNullable<ModalProps['size']>, string> = {
  sm: '480px',
  md: '640px',
  lg: '800px',
};

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(dialog: HTMLElement): HTMLElement[] {
  return Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((element) => !element.closest('[hidden], [aria-hidden="true"]'));
}

interface InertOwnership {
  count: number;
  originallyInert: boolean;
}

const modalStack: symbol[] = [];
const modalDialogs = new Map<symbol, HTMLElement>();
const inertOwnership = new Map<Element, InertOwnership>();
let bodyLockCount = 0;
let bodyOverflowBeforeModals = '';

function acquireInert(element: Element) {
  const current = inertOwnership.get(element);
  if (current) {
    current.count += 1;
    return;
  }
  const originallyInert = element.hasAttribute('inert');
  inertOwnership.set(element, { count: 1, originallyInert });
  if (!originallyInert) element.setAttribute('inert', '');
}

function releaseInert(element: Element) {
  const current = inertOwnership.get(element);
  if (!current) return;
  current.count -= 1;
  if (current.count > 0) return;
  inertOwnership.delete(element);
  if (!current.originallyInert) element.removeAttribute('inert');
}

function isTopModal(id: symbol): boolean {
  return modalStack[modalStack.length - 1] === id;
}

export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  children,
  footer,
  description,
  presentation = 'dialog',
  className,
}: ModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const modalIdRef = useRef(Symbol('modal'));
  const titleId = useId();
  const descriptionId = useId();

  // Keep the current callback without re-registering the document listener.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Trap focus, make the page behind the dialog inert, lock page scrolling,
  // and restore the invoking control on close.
  useEffect(() => {
    if (!open) return;

    const dialog = dialogRef.current;
    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const modalId = modalIdRef.current;
    modalStack.push(modalId);
    if (dialog) modalDialogs.set(modalId, dialog);
    if (bodyLockCount === 0) {
      bodyOverflowBeforeModals = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    bodyLockCount += 1;

    // Everything outside this modal's portal container becomes inert. Each
    // modal owns one reference so closing an underlying dialog cannot expose
    // the page while a newer dialog is still open.
    const container = containerRef.current;
    const inertedSiblings: Element[] = [];
    if (container?.parentElement) {
      for (const sibling of Array.from(container.parentElement.children)) {
        if (sibling === container) continue;
        acquireInert(sibling);
        inertedSiblings.push(sibling);
      }
    }

    // Focusing the dialog itself gives screen readers the title/context first;
    // the first Tab press then moves to the first actionable control.
    dialog?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (!isTopModal(modalId)) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab' || !dialog) return;

      const focusable = getFocusableElements(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || active === dialog || !dialog.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || active === dialog || !dialog.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const wasTop = isTopModal(modalId);
      const stackIndex = modalStack.lastIndexOf(modalId);
      if (stackIndex >= 0) modalStack.splice(stackIndex, 1);
      modalDialogs.delete(modalId);

      // Lift this modal's ownership before restoring focus — an inert subtree
      // refuses focus. Other open modals retain their own references.
      for (const sibling of inertedSiblings) releaseInert(sibling);

      bodyLockCount = Math.max(0, bodyLockCount - 1);
      if (bodyLockCount === 0) document.body.style.overflow = bodyOverflowBeforeModals;

      if (wasTop) {
        const previousFocus = previousFocusRef.current;
        const previousIsInert = previousFocus?.matches('[inert]') || previousFocus?.closest('[inert]');
        if (previousFocus?.isConnected && !previousIsInert) {
          previousFocus.focus();
        } else {
          const nextTop = modalStack[modalStack.length - 1];
          if (nextTop) modalDialogs.get(nextTop)?.focus();
        }
      }
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      ref={containerRef}
      className={['ui-modal', `ui-modal--${presentation}`, className ?? ''].filter(Boolean).join(' ')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: presentation === 'sheet' ? 'flex-end' : 'center',
        justifyContent: presentation === 'detail' ? 'flex-end' : 'center',
        padding: presentation === 'sheet' || presentation === 'detail' ? 0 : 'var(--space-phi-5)',
      }}
    >
      {/* Backdrop */}
      <div
        className="ui-modal__backdrop"
        aria-hidden="true"
        onClick={() => {
          if (isTopModal(modalIdRef.current)) onClose();
        }}
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'var(--color-scrim)',
          animation: `ui-modal-fade-in var(--motion-duration-sheet) var(--motion-easing) both`,
        }}
      />

      {/* Dialog panel */}
      <div
        className="ui-modal__panel modal-panel"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: presentation === 'detail' ? '560px' : WIDTH[size],
          maxHeight: presentation === 'dialog' ? '90dvh' : '100dvh',
          height: presentation === 'detail' ? '100dvh' : undefined,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--color-surface-overlay)',
          border: 'var(--border-width-default) solid var(--color-border)',
          borderRadius: presentation === 'detail' ? 'var(--radius-lg) 0 0 var(--radius-lg)' : 'var(--radius-lg)',
          boxShadow: 'var(--shadow-overlay)',
          outline: 'none',
          animation: `ui-modal-scale-in var(--motion-duration-sheet) var(--motion-easing) both`,
        }}
      >
        {/* Header */}
        <div className="ui-modal__header" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-5) var(--space-8)',
          borderBottom: 'var(--border-width-hairline) solid var(--color-divider)',
          flexShrink: 0,
        }}>
          <div>
            <h2
              id={titleId}
              className="ui-modal__title"
              style={{
                margin: 0,
                fontSize: 'var(--text-h2-size)',
                lineHeight: 'var(--text-h2-lh)',
                fontWeight: 'var(--text-h2-weight)',
                color: 'var(--color-text)',
              }}
            >
              {title}
            </h2>
            {description ? (
              <div
                id={descriptionId}
                className="ui-modal__description"
                style={{ marginTop: 'var(--space-1)', color: 'var(--color-text-secondary)', fontSize: 'var(--text-caption-size)' }}
              >
                {description}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="ui-modal__close"
            onClick={() => {
              if (isTopModal(modalIdRef.current)) onClose();
            }}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              width: 'var(--layout-touch-target)',
              height: 'var(--layout-touch-target)',
              padding: 0,
              color: 'var(--color-text-secondary)',
              fontSize: '20px',
              lineHeight: 1,
              borderRadius: 'var(--radius-sm)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {/* Body */}
        <div className="ui-modal__body" style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--space-phi-5) var(--space-8) var(--space-8)',
        }}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="ui-modal__footer" style={{
            flexShrink: 0,
            padding: 'var(--space-5) var(--space-8) var(--space-6)',
            borderTop: 'var(--border-width-hairline) solid var(--color-divider)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-3)',
          }}>
            {footer}
          </div>
        )}
      </div>

      <style>{`
        @keyframes ui-modal-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes ui-modal-scale-in {
          from { transform: scale(var(--motion-scale-press)); opacity: 0; }
          to   { transform: scale(1);    opacity: 1; }
        }
        @keyframes ui-modal-sheet-in {
          from { transform: translateY(var(--motion-distance-small)); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @media (max-width: 640px) {
          .ui-modal {
            align-items: flex-end !important;
            padding: 0 !important;
          }
          .ui-modal__panel {
            max-width: none !important;
            max-height: calc(100dvh - env(safe-area-inset-top, 0px) - var(--space-3)) !important;
            border-inline: 0 !important;
            border-bottom: 0 !important;
            border-radius: var(--radius-lg) var(--radius-lg) 0 0 !important;
            animation-name: ui-modal-sheet-in !important;
          }
          .ui-modal__header {
            min-height: 64px !important;
            padding: var(--space-4) var(--space-5) !important;
          }
          .ui-modal__title {
            font-size: clamp(20px, 6vw, var(--text-h2-size)) !important;
          }
          .ui-modal__close {
            width: var(--layout-touch-target) !important;
            height: var(--layout-touch-target) !important;
          }
          .ui-modal__body {
            padding: var(--space-5) !important;
            overscroll-behavior: contain;
          }
          .ui-modal__footer {
            padding: var(--space-4) var(--space-5) calc(var(--space-4) + env(safe-area-inset-bottom, 0px)) !important;
          }
        }
        .ui-modal--sheet .ui-modal__panel {
          max-width: 720px !important;
          max-height: min(88dvh, 900px) !important;
          border-radius: var(--radius-lg) var(--radius-lg) 0 0 !important;
          animation-name: ui-modal-sheet-in !important;
        }
        .ui-modal--detail .ui-modal__panel {
          animation-name: ui-modal-detail-in !important;
        }
        @keyframes ui-modal-detail-in {
          from { transform: translateX(var(--motion-distance-small)); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes ui-modal-fade-in  { from { opacity: 0; } to { opacity: 1; } }
          @keyframes ui-modal-scale-in { from { opacity: 0; } to { opacity: 1; } }
          @keyframes ui-modal-sheet-in { from { opacity: 0; } to { opacity: 1; } }
          @keyframes ui-modal-detail-in { from { opacity: 0; } to { opacity: 1; } }
        }
      `}</style>
    </div>,
    document.body,
  );
}
